import { useState } from "react";

interface PackageDownloadModalProps {
  onClose: () => void;
}

type PackageMode = "iframe" | "redirect";

export default function PackageDownloadModal({ onClose }: PackageDownloadModalProps) {
  const currentOrigin = window.location.origin;
  const [siteUrl, setSiteUrl] = useState(currentOrigin);
  const [siteTitle, setSiteTitle] = useState("我的网站");
  const [siteDescription, setSiteDescription] = useState("欢迎访问我的网站");
  const [faviconUrl, setFaviconUrl] = useState("");
  const [mode, setMode] = useState<PackageMode>("iframe");
  const [loading, setLoading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const generateIframeHtml = () => `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${siteTitle}</title>
  <meta name="description" content="${siteDescription}" />
  ${faviconUrl ? `<link rel="icon" href="${faviconUrl}" />` : ""}
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body { width: 100%; height: 100%; overflow: hidden; }
    iframe {
      width: 100%;
      height: 100%;
      border: none;
      display: block;
    }
  </style>
</head>
<body>
  <iframe
    src="${siteUrl}"
    title="${siteTitle}"
    allowfullscreen
    allow="fullscreen"
  ></iframe>
</body>
</html>`;

  const generateRedirectHtml = () => `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${siteTitle}</title>
  <meta name="description" content="${siteDescription}" />
  <meta http-equiv="refresh" content="0; url=${siteUrl}" />
  ${faviconUrl ? `<link rel="icon" href="${faviconUrl}" />` : ""}
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      display: flex; align-items: center; justify-content: center;
      min-height: 100vh; background: #f9fafb; color: #374151;
    }
    .container { text-align: center; }
    .spinner {
      width: 40px; height: 40px; border: 3px solid #e5e7eb;
      border-top-color: #f97316; border-radius: 50%;
      animation: spin 0.8s linear infinite; margin: 0 auto 16px;
    }
    @keyframes spin { to { transform: rotate(360deg); } }
    p { font-size: 14px; color: #6b7280; }
    a { color: #f97316; text-decoration: none; }
    a:hover { text-decoration: underline; }
  </style>
</head>
<body>
  <div class="container">
    <div class="spinner"></div>
    <p>正在跳转，请稍候...</p>
    <p style="margin-top:8px">如未自动跳转，请 <a href="${siteUrl}">点击这里</a></p>
  </div>
</body>
</html>`;

  const handleDownload = () => {
    setLoading(true);
    setTimeout(() => {
      const html = mode === "iframe" ? generateIframeHtml() : generateRedirectHtml();
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "index.html";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setLoading(false);
      setDownloaded(true);
      setTimeout(() => setDownloaded(false), 3000);
    }, 600);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-xl w-full max-w-lg mx-4 shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-orange-50 rounded-lg">
              <i className="ri-download-cloud-line text-orange-500 text-lg" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-800">网站打包下载</h2>
              <p className="text-xs text-gray-400 mt-0.5">生成可部署到其他服务器的静态 HTML 文件</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Mode selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">打包模式</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setMode("iframe")}
                className={`flex flex-col items-start gap-1.5 p-3.5 rounded-lg border-2 transition-all cursor-pointer text-left ${
                  mode === "iframe"
                    ? "border-orange-400 bg-orange-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`ri-layout-line text-base ${mode === "iframe" ? "text-orange-500" : "text-gray-400"}`} />
                  </div>
                  <span className={`text-sm font-medium ${mode === "iframe" ? "text-orange-600" : "text-gray-700"}`}>
                    iframe 嵌入
                  </span>
                </div>
                <p className="text-xs text-gray-400 leading-relaxed">
                  用 iframe 全屏嵌入网站，访客无感知，体验最佳
                </p>
              </button>
              <button
                onClick={() => setMode("redirect")}
                className={`flex flex-col items-start gap-1.5 p-3.5 rounded-lg border-2 transition-all cursor-pointer text-left ${
                  mode === "redirect"
                    ? "border-orange-400 bg-orange-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`ri-arrow-right-up-line text-base ${mode === "redirect" ? "text-orange-500" : "text-gray-400"}`} />
                  </div>
                  <span className={`text-sm font-medium ${mode === "redirect" ? "text-orange-600" : "text-gray-700"}`}>
                    跳转重定向
                  </span>
                </div>
                <p className="text-xs text-gray-400 leading-relaxed">
                  自动跳转到目标网站，适合 SEO 友好场景
                </p>
              </button>
            </div>
          </div>

          {/* Site URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              网站地址 <span className="text-red-400">*</span>
            </label>
            <input
              type="url"
              value={siteUrl}
              onChange={(e) => setSiteUrl(e.target.value)}
              placeholder="https://your-site.com"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
            />
            <p className="text-xs text-gray-400 mt-1">
              {mode === "iframe"
                ? "iframe 将加载此地址的内容，注意目标网站需允许被嵌入（X-Frame-Options）"
                : "访客打开 HTML 文件后将自动跳转到此地址"}
            </p>
          </div>

          {/* Site Title */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">页面标题</label>
              <input
                type="text"
                value={siteTitle}
                onChange={(e) => setSiteTitle(e.target.value)}
                placeholder="我的网站"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Favicon 地址</label>
              <input
                type="url"
                value={faviconUrl}
                onChange={(e) => setFaviconUrl(e.target.value)}
                placeholder="https://... （可选）"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">页面描述</label>
            <input
              type="text"
              value={siteDescription}
              onChange={(e) => setSiteDescription(e.target.value)}
              placeholder="网站描述（用于 SEO meta description）"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
            />
          </div>

          {/* Info tip */}
          <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-100 rounded-lg px-4 py-3">
            <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
              <i className="ri-information-line text-amber-500 text-sm" />
            </div>
            <p className="text-xs text-amber-700 leading-relaxed">
              下载的 <strong>index.html</strong> 是一个静态文件，将其上传到任意 Web 服务器或静态托管平台（如 Nginx、GitHub Pages、Vercel）即可使用。网站数据仍来自原始服务器，无需迁移数据库。
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
          <div className="text-xs text-gray-400">
            生成文件：<code className="bg-gray-200 px-1.5 py-0.5 rounded text-gray-600">index.html</code>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              取消
            </button>
            <button
              onClick={handleDownload}
              disabled={!siteUrl || loading}
              className={`flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                downloaded
                  ? "bg-green-500 text-white"
                  : "bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                {loading ? (
                  <i className="ri-loader-4-line text-sm animate-spin" />
                ) : downloaded ? (
                  <i className="ri-check-line text-sm" />
                ) : (
                  <i className="ri-download-2-line text-sm" />
                )}
              </div>
              <span>{loading ? "生成中..." : downloaded ? "下载成功！" : "下载 HTML 包"}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
